import { useId, useState } from 'react'
import type { EvidenceSlotCitationDTO } from '@deepguide-ai/types'
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from '@deepguide-ai/ui/hover-card'
import { cn } from '@/lib/utils'
import { getFrameworkDisplayName } from '@/hooks/useComplianceFramework'

/**
 * Framework requirement chips (e.g. "HIPAA:§164.402"), capped at `max` with a
 * "+N" overflow that reveals the remaining refs on hover. Shared by the upload
 * picker (open-slot row + consolidation checklist) and the policies table so
 * the chip + overflow behavior stays identical. Renders nothing when empty.
 */
export function FrameworkRefChips({
  refs,
  max = 4,
  className,
  separator = ':',
  groupByFramework = false,
}: {
  refs: { framework: string; refs: string[] }[]
  max?: number
  className?: string
  separator?: string
  /** One framework/count pill with its full reference list on hover or click. */
  groupByFramework?: boolean
}) {
  const [openFramework, setOpenFramework] = useState<string | null>(null)
  const contentId = useId()
  if (groupByFramework) {
    const grouped = new Map<string, Set<string>>()
    for (const group of refs) {
      const name = getFrameworkDisplayName(group.framework)
      const citations = grouped.get(name) ?? new Set<string>()
      group.refs
        .filter((ref) => ref.trim())
        .forEach((ref) => citations.add(ref))
      if (citations.size > 0) grouped.set(name, citations)
    }
    if (grouped.size === 0) return null
    return (
      <div className={cn('flex flex-wrap items-center gap-2', className)}>
        {[...grouped].map(([framework, citations]) => (
          <HoverCard
            key={framework}
            open={openFramework === framework}
            onOpenChange={(open) =>
              setOpenFramework((current) =>
                open ? framework : current === framework ? null : current
              )
            }
            openDelay={80}
            closeDelay={100}
          >
            <HoverCardTrigger asChild>
              <button
                type='button'
                aria-expanded={openFramework === framework}
                aria-controls={`${contentId}-${encodeURIComponent(framework)}`}
                onClick={() => setOpenFramework(framework)}
                className='border-status-blue-border bg-status-blue-subtle text-status-blue-text focus-visible:ring-ring inline-flex items-center rounded-sm border px-1.5 py-0.5 text-[11px] font-normal focus-visible:ring-2 focus-visible:outline-none'
              >
                {framework} ({citations.size})
              </button>
            </HoverCardTrigger>
            <HoverCardContent
              id={`${contentId}-${encodeURIComponent(framework)}`}
              align='start'
              className='w-auto max-w-[min(360px,calc(100vw-2rem))] p-3'
            >
              <p className='mb-2 text-xs font-medium'>{framework} citations</p>
              <ul className='text-text-subtle max-h-64 space-y-1 overflow-y-auto text-xs'>
                {[...citations].map((citation) => (
                  <li key={citation}>{citation}</li>
                ))}
              </ul>
            </HoverCardContent>
          </HoverCard>
        ))}
      </div>
    )
  }
  const badges = refs.flatMap((fr) =>
    fr.refs.map((ref) => ({
      key: `${fr.framework}:${ref}`,
      label: `${getFrameworkDisplayName(fr.framework)}${separator}${ref}`,
    }))
  )
  if (badges.length === 0) return null
  const shown = badges.slice(0, max)
  const hidden = badges.slice(max)

  const chip = (b: { key: string; label: string }) => (
    <span
      key={b.key}
      // h-5 matches the Badge primitive chips (TestKindBadge, KeptInScopeBadge)
      // these sit next to in list rows — one chip height per metadata line.
      className='border-cobalt-strong/30 bg-cobalt-tonal text-cobalt-strong font-heading inline-flex h-5 items-center rounded-sm border px-1.5 text-[9px] font-medium'
    >
      {b.label}
    </span>
  )

  return (
    <div className={cn('mt-1 flex flex-wrap items-center gap-1', className)}>
      {shown.map(chip)}
      {hidden.length > 0 && (
        <HoverCard openDelay={80} closeDelay={60}>
          <HoverCardTrigger asChild>
            <span className='font-heading text-text-placeholder hover:text-cobalt-strong inline-flex h-5 cursor-default items-center rounded-sm px-1 text-[9px] font-medium'>
              +{hidden.length}
            </span>
          </HoverCardTrigger>
          <HoverCardContent align='start' className='w-auto max-w-[280px] p-2'>
            <div className='font-body text-text-subtle mb-1 text-[10px] font-medium tracking-[0.4px] uppercase'>
              {hidden.length} more requirement{hidden.length === 1 ? '' : 's'}
            </div>
            <div className='flex flex-wrap items-center gap-1'>
              {hidden.map(chip)}
            </div>
          </HoverCardContent>
        </HoverCard>
      )}
    </div>
  )
}

/** Render the flat citation shape shared by EvidenceSlotDef and CES DTOs. */
export function FrameworkCitationChips({
  citations,
  max = 4,
  className,
  showInclusion = false,
  groupByFramework = false,
}: {
  citations: EvidenceSlotCitationDTO[]
  max?: number
  className?: string
  showInclusion?: boolean
  groupByFramework?: boolean
}) {
  return (
    <FrameworkRefChips
      refs={(showInclusion
        ? [
            ...new Map(
              citations.map((citation) => [citation.frameworkSlug, citation])
            ).values(),
          ]
        : citations
      ).map((citation) => ({
        framework: citation.frameworkName,
        refs: [
          showInclusion
            ? citation.inclusion
              ? {
                  core: 'Core',
                  scope: 'Scope-dependent',
                  extended: 'Additional',
                }[citation.inclusion]
              : 'Mapped'
            : citation.ref,
        ],
      }))}
      separator={showInclusion ? ' · ' : ':'}
      max={max}
      className={className}
      groupByFramework={groupByFramework}
    />
  )
}
